import { zodResolver } from '@hookform/resolvers/zod';
import {
  Dialog,
  Form,
  FormButton,
  Input,
  useNotifications,
  validatePassword
} from '@ijoin/components';
import { usePatchAdminLoginResetPassword } from '@ijoin/hooks';
import {
  Button,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Typography
} from '@mui/material';

import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

type ResetPasswordDialogProps = {
  isOpen: boolean;
  loginId: string;
  onClose: () => void;
  username: string;
};

export const ResetPasswordDialog = (props: ResetPasswordDialogProps) => {
  const notifications = useNotifications();
  const [mode, setMode] = useState<'direct' | 'email'>('direct');

  const passwordResolver = zodResolver(
    z.object({
      password: validatePassword([props.username], { minLength: 16 })
    })
  );

  const methods = useForm({
    defaultValues: { password: '' },
    mode: 'all',
    resolver: passwordResolver
  });

  const resetPassword = usePatchAdminLoginResetPassword({
    mutation: {
      onError: () => notifications.error('Failed to reset password'),
      onSuccess: () => {
        onClose();
        notifications.success('Password reset successfully');
      }
    }
  });

  const onClose = () => {
    methods.reset();
    setMode('direct');
    props.onClose();
  };

  const onSubmit = (data: { password: string }) => {
    resetPassword.mutate({
      data: { mode: 'direct' as const, password: data.password },
      loginId: props.loginId
    });
  };

  const onSendEmail = () => {
    resetPassword.mutate({
      data: { mode: 'email' as const },
      loginId: props.loginId
    });
  };

  return (
    <Dialog
      data-testid='resetPasswordDialog'
      isOpen={props.isOpen}
      onClose={onClose}>
      <DialogTitle variant='h3'>Reset Password</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <Typography variant='body2'>
            Reset password for {props.username}
          </Typography>

          <ToggleButtonGroup
            data-testid='resetMode'
            exclusive
            onChange={(_, value) => {
              if (value) setMode(value);
            }}
            value={mode}>
            <ToggleButton data-testid='directMode' value='direct'>
              Direct Set
            </ToggleButton>
            <ToggleButton data-testid='emailMode' value='email'>
              Send Email
            </ToggleButton>
          </ToggleButtonGroup>

          {mode === 'direct' && (
            <Form
              methods={methods}
              noValidate
              onSubmit={methods.handleSubmit(onSubmit)}>
              <Stack spacing={2}>
                <Input
                  data-1p-ignore
                  data-testid='newPassword'
                  format='password'
                  fullWidth
                  label='New Password'
                  name='password'
                  size='small'
                />
                <DialogActions sx={{ p: 0 }}>
                  <Button
                    data-testid='cancelReset'
                    onClick={onClose}
                    variant='outlined'>
                    Cancel
                  </Button>
                  <FormButton
                    data-testid='confirmReset'
                    triggerValidation
                    variant='contained'>
                    Set Password
                  </FormButton>
                </DialogActions>
              </Stack>
            </Form>
          )}

          {mode === 'email' && (
            <>
              <Typography color='text.secondary' variant='body2'>
                A password reset email will be sent to the user.
              </Typography>
              <DialogActions sx={{ p: 0 }}>
                <Button
                  data-testid='cancelReset'
                  onClick={onClose}
                  variant='outlined'>
                  Cancel
                </Button>
                <Button
                  data-testid='confirmReset'
                  disabled={resetPassword.isPending}
                  onClick={onSendEmail}
                  variant='contained'>
                  Send Email
                </Button>
              </DialogActions>
            </>
          )}
        </Stack>
      </DialogContent>
    </Dialog>
  );
};
